// show error message in a model when limits are exceeded

import { Dispatch } from "react";
import { Button, Modal, Card, Badge } from "react-bootstrap";
import { FiAlertTriangle, FiZap, FiHelpCircle, FiCheck, FiX } from "react-icons/fi";
import { IOrg } from "../../../models/org";
import { Link } from "react-router-dom";

type Props = {
    orgId?: IOrg;
    show: boolean;
    setShow: Dispatch<React.SetStateAction<boolean>>;
}

function LimitsExceeded({ show, setShow }: Props) {
    return (
        <Modal show={show} onHide={()=>setShow(false)} size="lg" centered>
            <Modal.Header closeButton className="bg-dark text-white border-secondary">
                <Modal.Title className="d-flex align-items-center">
                    <div className="bg-warning bg-opacity-25 p-2 rounded-circle me-3">
                        <FiAlertTriangle className="text-warning" size={20} />
                    </div>
                    <div>
                        <h5 className="mb-0 text-white">Plan Limits Exceeded</h5>
                        <small className="text-light">Action required to continue</small>
                    </div>
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="p-0 bg-dark">
                <div className="p-4">
                    <Card className="bg-dark border-warning mb-4">
                        <Card.Header className="bg-dark border-warning">
                            <div className="d-flex align-items-center">
                                <FiZap className="text-warning me-2" size={18} />
                                <h6 className="text-white mb-0">Usage Limit Reached</h6>
                                <Badge bg="warning" text="dark" className="ms-auto">
                                    Limit Exceeded
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="bg-dark p-0">
                            <div className="p-3 border-bottom border-secondary">
                                <div className="d-flex align-items-start">
                                    <div className="bg-warning bg-opacity-25 p-2 rounded-circle me-3 mt-1">
                                        <FiAlertTriangle className="text-warning" size={16} />
                                    </div>
                                    <div className="flex-grow-1">
                                        <h6 className="text-white mb-2 fw-semibold">
                                            Cannot Downgrade Plan
                                        </h6>
                                        <p className="text-light mb-0">
                                            Your current usage exceeds the selected plan limits. You cannot downgrade to a plan with lower limits than your current usage.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="p-3">
                                <div className="d-flex align-items-start">
                                    <div className="bg-primary bg-opacity-25 p-2 rounded-circle me-3 mt-1">
                                        <FiZap className="text-primary" size={16} />
                                    </div>
                                    <div className="flex-grow-1">
                                        <h6 className="text-white mb-2 fw-semibold">
                                            Available Options
                                        </h6>
                                        <p className="text-light mb-0">
                                            Please upgrade your plan to continue using the service, or reduce your usage to fit within the new plan limits if you wish to downgrade.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="bg-info bg-opacity-25 border-info mb-4">
                        <Card.Body className="p-3">
                            <div className="d-flex align-items-center">
                                <div className="bg-info bg-opacity-50 p-2 rounded-circle me-3">
                                    <FiHelpCircle className="text-white" size={16} />
                                </div>
                                <div>
                                    <h6 className="text-white mb-1 fw-semibold">
                                        Need Assistance?
                                    </h6>
                                    <p className="mb-0 text-light">
                                        Contact our{' '}
                                        <Link 
                                            to={"/dashboard/support"} 
                                            className="text-primary text-decoration-none fw-semibold"
                                        >
                                            support team
                                        </Link>
                                        {' '}for help with your plan.
                                    </p>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </Modal.Body>
            
            <Modal.Footer className="bg-dark border-secondary p-4">
                <div className="d-flex justify-content-end w-100">
                    <Button 
                        variant="primary" 
                        onClick={() => setShow(false)}
                        className="d-flex align-items-center gap-2 px-4 fw-semibold"
                    >
                        <FiCheck size={16} />
                        I Understand
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default LimitsExceeded